import { Descope } from '@descope/react-sdk/flows';
import React, { useState } from 'react';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import {
  ADD_MFA_WITH_TOTP_INTERACTION_IDS,
  AddMfaWithTotpScreenNames,
} from './screenMapping';
import TotpSetupVerificationScreen from './TotpSetupVerificationScreen';

export const ADD_MFA_WITH_TOTP_FLOW_ID = 'add-mfa-with-totp';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;
type RawDescopeNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type FlowForm = Record<string, unknown>;
type DescopeContext = {
  error?: unknown;
  form?: FlowForm;
  totp?: unknown;
  [key: string]: unknown;
};
type FlowState = DescopeContext & {
  next?: DescopeNext;
  screenName?: string;
};

const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);
  url.hash = '';
  return url.toString();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const getErrorText = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  return typeof error.text === 'string' ? error.text : undefined;
};

const getErrorDetail = (error: unknown): unknown =>
  isRecord(error) && 'detail' in error ? error.detail : error;

const getTotpImageSrc = (totp: unknown): string | undefined => {
  if (!isRecord(totp)) {
    return undefined;
  }

  if (typeof totp.image !== 'string') {
    return undefined;
  }

  return totp.image.startsWith('data:')
    ? totp.image
    : `data:image/jpg;base64,${totp.image}`;
};

const resetErrorState = (
  setState: React.Dispatch<React.SetStateAction<FlowState>>
) =>
  setState(prevState => ({
    ...prevState,
    error: {},
  }));

const AddMfaWithTotpFlow: React.FC = () => {
  const [state, setState] = useState<FlowState>({ error: {} });
  const [form, setForm] = useState<FlowForm>({});
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const flowForm = {
    ...(state?.form ?? {}),
    ...form,
  };

  return (
    <Descope
      flowId={ADD_MFA_WITH_TOTP_FLOW_ID}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(flowForm)}
      onScreenUpdate={(
        screenName: string,
        context: DescopeContext,
        next: RawDescopeNext
      ) => {
        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));

        setState(prevState => ({
          ...prevState,
          ...context,
          next: nextWithClientSessionId,
          screenName,
          error: context?.error || {},
        }));

        return screenName === AddMfaWithTotpScreenNames.VERIFY_TOTP;
      }}
      onSuccess={() => {
        window.location.href = `${LINKTREE_BASE_URL}/admin/account`;
      }}
      onError={(error: unknown) => {
        setState(prevState => ({
          ...prevState,
          error: getErrorDetail(error),
        }));
      }}
    >
      {state?.screenName === AddMfaWithTotpScreenNames.VERIFY_TOTP && (
        <TotpSetupVerificationScreen
          authFlow="signup"
          mfaFlow="enrollment"
          totpImageSrc={getTotpImageSrc(state.totp)}
          onFormUpdate={data => setForm(prevForm => ({ ...prevForm, ...data }))}
          onCodeSubmit={async (code: string) => {
            if (state.next) {
              await state.next(ADD_MFA_WITH_TOTP_INTERACTION_IDS.SUBMIT_CODE, {
                ...flowForm,
                code,
              });
            }
          }}
          errorText={getErrorText(state.error)}
          resetError={() => resetErrorState(setState)}
        />
      )}
    </Descope>
  );
};

export default AddMfaWithTotpFlow;
